import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { makeCommaSeparatedString } from '@app/utils/stringUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReorderIcon from '@mui/icons-material/Reorder';
import { IconButton, Typography } from '@mui/material';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { ACTION_MODE } from '../../ListTaxonomies';
import { TaxonomyPartnerHeroImage } from '../TaxonomyPartnerHeroImage';

interface TaxonomyInfoProps {
  selectedTaxonomy: GenericObject | null;
  onDelete(row: GenericObject | null): void;
  handleChangeActionMode(action: string): void;
}

const properties = [
  { key: 'category_name', label: 'Topic Name' },
  { key: 'nav_label', label: 'Nav Label' },
  {
    key: 'related_topic_names',
    label: 'Related Topics',
    format: (taxonomy: GenericObject | null) => {
      if (!taxonomy?.related_topic_names) {
        return '';
      }

      const stringArray = (taxonomy.related_topic_names as string[]).map((item: string) => {
        const jsonObj = JSON.parse(item);

        return getFieldOnMainLanguage(jsonObj);
      });

      return makeCommaSeparatedString(stringArray);
    },
  },
  { key: 'seo_title', label: 'SEO Title' },
  { key: 'meta_description', label: 'Meta Description' },
];

export const TaxonomyInfo = ({ selectedTaxonomy, onDelete, handleChangeActionMode }: TaxonomyInfoProps) => {
  const ability = useAbility();
  const taxonomyId = selectedTaxonomy?.id as number | undefined;
  const showPartnerHeroImageUpload =
    (taxonomyId === 252 || taxonomyId === 258) && ability.can(CASLAction.WRITE, 'partner-taxonomies');

  return (
    <div>
      <div style={{ padding: '30px 50px' }}>
        <div style={{ display: 'flex', marginBottom: '24px', height: '30px' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold', width: '200px' }} color="text.secondary" gutterBottom>
            Parent Topic
          </Typography>
          <Typography
            data-cy="parent_category_name"
            sx={{ fontSize: '13px', flex: 1 }}
            color="text.secondary"
            gutterBottom>
            {selectedTaxonomy?.parent_category_name
              ? getFieldOnMainLanguage(selectedTaxonomy?.parent_category_name)
              : ''}
          </Typography>
          {ability.can(CASLAction.WRITE, 'taxonomies') && (
            <div style={{ display: 'flex' }}>
              <IconButton onClick={() => handleChangeActionMode(ACTION_MODE.REORDER)} size="small" aria-label="reorder">
                <ReorderIcon fontSize="small" />
              </IconButton>
              <IconButton
                style={{ marginLeft: '12px' }}
                onClick={() => handleChangeActionMode(ACTION_MODE.EDIT)}
                size="small"
                aria-label="edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => onDelete(selectedTaxonomy)}
                size="small"
                style={{ marginLeft: '12px' }}
                color="error"
                aria-label="delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </div>
        {properties.map((property) => (
          <div style={{ display: 'flex', marginBottom: '24px', height: '30px' }}>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 'bold', minWidth: '200px' }}
              color="text.secondary"
              gutterBottom>
              {property.label}
            </Typography>
            <Typography data-cy={property.key} sx={{ fontSize: '13px' }} color="text.secondary" gutterBottom>
              {property.format
                ? property.format(selectedTaxonomy)
                : selectedTaxonomy && selectedTaxonomy[property.key]
                ? getFieldOnMainLanguage(selectedTaxonomy[property.key])
                : ''}
            </Typography>
          </div>
        ))}
        {showPartnerHeroImageUpload && <TaxonomyPartnerHeroImage taxonomyId={taxonomyId} />}
      </div>
    </div>
  );
};
